import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/Common";
import { GaugeCircle } from "lucide-react";

/**
 * Fetches /approval-policies/resolve whenever (type, amount, branch) change
 * and renders a banner showing which approvers will be auto-selected. Also
 * exposes the resolved approver ids via `onResolved` so the parent form can
 * pre-fill its selects.
 */
export default function PolicyPreview({ type, amount, branch, onResolved }) {
  const [state, setState] = useState({ status: "idle" });
  const t = useRef();
  const previousIdsRef = useRef("");

  useEffect(() => {
    clearTimeout(t.current);
    if (!type) { setState({ status: "idle" }); return; }
    t.current = setTimeout(async () => {
      setState({ status: "loading" });
      try {
        const params = new URLSearchParams({
          type,
          amount: String(Number(amount) || 0),
        });
        if (branch) params.append("branch", branch);
        const { data } = await api.get(`/approval-policies/resolve?${params.toString()}`);
        setState({ status: "done", data });
        // Only push to parent if the list of ids changed — prevents infinite loops.
        const ids = (data.approvers || []).join(",");
        if (onResolved && ids !== previousIdsRef.current) {
          previousIdsRef.current = ids;
          onResolved(data);
        }
      } catch { setState({ status: "error" }); }
    }, 300);
    return () => clearTimeout(t.current);
  }, [type, amount, branch]);

  if (state.status === "idle") return null;
  if (state.status === "loading") {
    return <div className="text-[11px] text-gray-500 italic">Checking approval policy…</div>;
  }
  if (state.status === "error") return null;
  const d = state.data;
  if (!d?.resolved) {
    return (
      <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
        data-testid="policy-preview-none">
        No approval policy matches this type &amp; amount — pick approvers manually below.
      </div>
    );
  }
  return (
    <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-2 flex items-start gap-2"
      data-testid="policy-preview-resolved">
      <GaugeCircle size={14} className="text-emerald-800 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-emerald-900 font-semibold">
          Policy auto-selects {(d.approver_names || []).length} approver(s){d.label ? ` · ${d.label}` : ""}
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          {(d.approver_names || []).map((n, i) => (
            <Badge key={i} tone="emerald">{i + 1}. {n}</Badge>
          ))}
        </div>
      </div>
    </div>
  );
}
